import MSCartesian from '@fusioncharts/charts/src/chart/_internal/mscartesian';
import logAxisFactory from '../../factories/log-axis';
import { pluckNumber, pluck, chartPaletteStr, pluckFontSizeMaintainUnit } from '@fusioncharts/core/src/lib';
import isEqualString from '@fusioncharts/utils/src/string/equal';

class MsLog extends MSCartesian {
  constructor () {
    super();
    this.registerFactory('axis', logAxisFactory, ['canvas']);
  }
  /**
   * Provides the name of the chart extension
   *
   * @static
   * @return {string} The name of the chart extension
   */
  static getName () {
    return 'MsLog';
  }

  _feedAxesRawData () {
    var iapi = this,
      dataObj = iapi.getFromEnv('dataSource'),
      chartAttrs = iapi.getFromEnv('chart-attrib'),
      xAxisConf,
      yAxisConf;

    xAxisConf = iapi.getSpecificxAxisConf();
    yAxisConf = iapi.getSpecificyAxisConf();

    if (yAxisConf.base === '1') {
      yAxisConf.base = 10;
      yAxisConf.logBase = 10;
    }
    yAxisConf.minorDivlinecolor = pluck(chartAttrs.minordivlinecolor, yAxisConf.divLineColor);
    yAxisConf.minorDivLineThickness = pluck(chartAttrs.minordivlinethickness, 1);
    yAxisConf.minorDivLineAlpha = pluck(chartAttrs.minordivlinealpha,
      yAxisConf.divLineAlpha / 2);
    xAxisConf.vtrendlines = dataObj.vtrendlines;
    yAxisConf.trendlines = dataObj.trendlines;

    return {
      yAxisConf: [yAxisConf],
      xAxisConf: [xAxisConf]
    };
  }

  /**
   * Returns x-axis configuration for chart.
   * @return {Object} configurations of x-axis.
   */
  getSpecificxAxisConf () {
    var iapi = this,
      chartAttrs = iapi.getFromEnv('chart-attrib'),
      xAxisConf = iapi.getBasexAxisConf();

    xAxisConf.showZeroPlane = chartAttrs.showvzeroplane;
    xAxisConf.zeroPlaneThickness = chartAttrs.vzeroplanethickness;
    xAxisConf.zeroPlaneAlpha = chartAttrs.vzeroplanealpha;
    xAxisConf.showAxisLine = pluckNumber(chartAttrs.showxaxisline, chartAttrs.showaxislines, chartAttrs.drawAxisLines, 0);
    xAxisConf.axisLineColor = pluck(chartAttrs.xaxislinecolor, chartAttrs.axislinecolor, '#000000');
    xAxisConf.isReverse = false;
    xAxisConf.showAlternateGridColor = pluckNumber(chartAttrs.showalternatevgridcolor, 0);
    xAxisConf.numDivLines = chartAttrs.numvdivlines;
    xAxisConf.maxLabelHeight = chartAttrs.maxlabelheight;
    xAxisConf.axisName = chartAttrs.xaxisname;
    xAxisConf.setAdaptiveMin = chartAttrs.setadaptivexmin;
    xAxisConf.showLimits = chartAttrs.showvlimits;
    xAxisConf.showDivLineValues = pluckNumber(chartAttrs.showvdivlinevalues, chartAttrs.showvdivlinevalues);

    return xAxisConf;
  }

  /**
   * Returns y-axis configuration for chart.
   * @return {Object} configurations of y-axis.
   */
  getSpecificyAxisConf () {
    var iapi = this,
      chartAttrs = iapi.getFromEnv('chart-attrib'),
      palleteString = iapi.config.is3D ? chartPaletteStr.chart3D : chartPaletteStr.chart2D,
      invertYAxis = !!pluckNumber(chartAttrs.invertyaxis, 0), // eslint-disable-line good-practices/no-single-usage-variable
      colorM = iapi.getFromEnv('color-manager'),
      outCanfontSizeWithUnit = pluckFontSizeMaintainUnit(chartAttrs.outcnvbasefontsize, chartAttrs.basefontsize, 10);

    return {
      isVertical: true,
      isReverse: !invertYAxis,
      isOpposit: isEqualString(chartAttrs.yaxisposition, 'right'),
      outCanfontFamily: pluck(chartAttrs.outcnvbasefont, chartAttrs.basefont, 'Verdana,sans'),
      outCanfontSize: this.computeFontSize(outCanfontSizeWithUnit),
      outCanfontSizeWithUnit: outCanfontSizeWithUnit,
      outCancolor: pluck(chartAttrs.outcnvbasefontcolor, chartAttrs.basefontcolor,
        colorM.getColor(palleteString.baseFontColor)).replace(/^#? ([a-f0-9]+)/ig, '#$1'),
      axisNamePadding: chartAttrs.yaxisnamepadding,
      axisValuePadding: chartAttrs.yaxisvaluespadding,
      axisNameFont: chartAttrs.yaxisnamefont,
      axisNameFontSize: this.computeFontSize(chartAttrs.yaxisnamefontsize),
      axisNameFontSizeWithUnit: chartAttrs.yaxisnamefontsize,
      axisNameFontColor: chartAttrs.yaxisnamefontcolor,
      axisNameFontBold: chartAttrs.yaxisnamefontbold,
      axisNameFontItalic: chartAttrs.yaxisnamefontitalic,
      axisNameBgColor: chartAttrs.yaxisnamebgcolor,
      axisNameBorderColor: chartAttrs.yaxisnamebordercolor,
      axisNameAlpha: chartAttrs.yaxisnamealpha,
      axisNameFontAlpha: chartAttrs.yaxisnamefontalpha,
      axisNameBgAlpha: chartAttrs.yaxisnamebgalpha,
      axisNameBorderAlpha: chartAttrs.yaxisnameborderalpha,
      axisNameBorderPadding: chartAttrs.yaxisnameborderpadding,
      axisNameBorderRadius: chartAttrs.yaxisnameborderradius,
      axisNameBorderThickness: chartAttrs.yaxisnameborderthickness,
      axisNameBorderDashed: chartAttrs.yaxisnameborderdashed,
      axisNameBorderDashLen: chartAttrs.yaxisnameborderdashlen,
      axisNameBorderDashGap: chartAttrs.yaxisnameborderdashgap,
      axisNameWidth: chartAttrs.yaxisnamewidth,
      useEllipsesWhenOverflow: chartAttrs.useellipseswhenoverflow,
      rotateAxisName: pluckNumber(chartAttrs.rotateyaxisname, 1),
      axisName: chartAttrs.yaxisname,
      divLineColor: pluck(chartAttrs.divlinecolor, colorM.getColor(palleteString.divLineColor)),
      divLineAlpha: pluck(chartAttrs.divlinealpha, colorM.getColor('divLineAlpha')),
      divLineThickness: pluckNumber(chartAttrs.divlinethickness, 2),
      divLineIsDashed: Boolean(pluckNumber(chartAttrs.divlinedashed, chartAttrs.divlineisdashed, 0)),
      divLineDashLen: pluckNumber(chartAttrs.divlinedashlen, 4),
      divLineDashGap: pluckNumber(chartAttrs.divlinedashgap, 2),
      showAlternateGridColor: pluckNumber(chartAttrs.showalternatehgridcolor, 1),
      alternateGridColor: pluck(chartAttrs.alternatehgridcolor, colorM.getColor('altHGridColor')),
      alternateGridAlpha: pluck(chartAttrs.alternatehgridalpha, colorM.getColor('altHGridAlpha')),
      numDivLines: chartAttrs.numdivlines,
      axisMinValue: chartAttrs.yaxisminvalue,
      axisMaxValue: chartAttrs.yaxismaxvalue,
      setAdaptiveMin: chartAttrs.setadaptiveymin,
      adjustDiv: chartAttrs.adjustdiv,
      labelStep: chartAttrs.yaxisvaluesstep,
      showAxisValues: pluckNumber(chartAttrs.showyaxisvalues, chartAttrs.showyaxisvalue),
      showLimits: pluckNumber(chartAttrs.showyaxislimits, chartAttrs.showlimits, iapi.showLimits),
      showDivLineValues: pluckNumber(chartAttrs.showdivlinevalues, chartAttrs.showdivlinevalue),
      showZeroPlane: chartAttrs.showzeroplane,
      zeroPlaneColor: chartAttrs.zeroplanecolor,
      zeroPlaneThickness: chartAttrs.zeroplanethickness,
      zeroPlaneAlpha: chartAttrs.zeroplanealpha,
      showZeroPlaneValue: chartAttrs.showzeroplanevalue,
      trendlineColor: chartAttrs.trendlinecolor,
      trendlineToolText: chartAttrs.trendlinetooltext,
      trendlineThickness: chartAttrs.trendlinethickness,
      trendlineAlpha: chartAttrs.trendlinealpha,
      showTrendlinesOnTop: chartAttrs.showtrendlinesontop,
      showAxisLine: pluckNumber(chartAttrs.showyaxisline, chartAttrs.showaxislines,
        chartAttrs.drawAxisLines, 0),
      axisLineThickness: pluckNumber(chartAttrs.yaxislinethickness, chartAttrs.axislinethickness, 1),
      axisLineAlpha: pluckNumber(chartAttrs.yaxislinealpha, chartAttrs.axislinealpha, 100),
      base: chartAttrs.base,
      logBase: chartAttrs.logbase,
      axisLineColor: pluck(chartAttrs.yaxislinecolor, chartAttrs.axislinecolor, '#000000'),
      showMinorDivLineValues: pluckNumber(chartAttrs.showminordivlinevalues, 0),
      numMinorDivLines: chartAttrs.numminordivlines,
      labelFont: chartAttrs.yaxisvaluefont,
      labelFontSize: this.computeFontSize(chartAttrs.yaxisvaluefontsize),
      labelFontSizeWithUnit: chartAttrs.yaxisvaluefontsize,
      labelFontColor: chartAttrs.yaxisvaluefontcolor,
      labelAlpha: chartAttrs.yaxisvaluealpha,
      labelFontBold: chartAttrs.yaxisvaluefontbold,
      labelFontItalic: chartAttrs.yaxisvaluefontitalic,
      labelBdrColor: chartAttrs.yaxisvaluebordercolor,
      labelBdrAlpha: chartAttrs.yaxisvalueborderalpha,
      labelBgAlpha: chartAttrs.yaxisvaluebgalpha,
      labelLink: chartAttrs.yaxisvaluelink,
      labelBgColor: chartAttrs.yaxisvaluebgcolor,
      labelBdrThickness: chartAttrs.yaxisvalueborderthickness,
      labelBdrPadding: chartAttrs.yaxisvalueborderpadding,
      labelBdrRadius: chartAttrs.yaxisvalueborderradius,
      labelBdrDashed: chartAttrs.yaxisvalueborderdashed,
      labelBdrDashLen: chartAttrs.yaxisvalueborderdashlen,
      labelBdrDashGap: chartAttrs.yaxisvalueborderdashgap
    };
  }
}
export default MsLog;
